import React, { useState, useEffect, useRef } from 'react';
import managedAxios, { isCancelError } from "@/utils/managedAxios";
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { 
  Clock, 
  CheckCircle, 
  XCircle, 
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  Filter,
  Shield,
  Lightbulb,
  Lock
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { API_BASE } from '@/App';

/**
 * CLARIAS ERCA (Exam-Relevant Current Affairs) Page
 * 
 * Philosophy:
 * - Reading more CA does NOT increase Prelims probability
 * - UPSC tests application, not recall
 * - Most current affairs are safely ignorable
 * 
 * This engine grants permission to ignore safely.
 * Current affairs are a tool, not a task.
 */

// ============================================================================
// CONSTANTS
// ============================================================================

const MAX_READ_TIME = 180; // 3 minutes in seconds
const DAILY_CAP = 3;

const RELEVANCE_STYLES = {
  high: { 
    bg: 'bg-emerald-100', 
    text: 'text-emerald-700', 
    border: 'border-emerald-300',
    icon: '🟢',
    label: 'High',
    description: 'Direct application possible'
  },
  moderate: { 
    bg: 'bg-amber-100', 
    text: 'text-amber-700', 
    border: 'border-amber-300',
    icon: '🟡',
    label: 'Moderate',
    description: 'Useful only for elimination'
  },
  low: { 
    bg: 'bg-red-100', 
    text: 'text-red-700', 
    border: 'border-red-300',
    icon: '🔴',
    label: 'Low',
    description: 'Safe to ignore'
  }
};

// ============================================================================
// ERCA ITEM COMPONENT
// ============================================================================

const ERCAItemCard = ({ item, onComplete }) => {
  const [timeElapsed, setTimeElapsed] = useState(0);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const timerRef = useRef(null);

  const relevanceStyle = RELEVANCE_STYLES[item.relevance] || RELEVANCE_STYLES.moderate;

  useEffect(() => {
    // Start timer
    timerRef.current = setInterval(() => {
      setTimeElapsed(prev => {
        const newTime = prev + 1;
        if (newTime >= MAX_READ_TIME && !isCollapsed) {
          setIsCollapsed(true);
        }
        return newTime;
      });
    }, 1000);

    return () => clearInterval(timerRef.current);
  }, []);

  const handleAnswerSubmit = () => {
    setShowResult(true);
    clearInterval(timerRef.current);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const timeProgress = Math.min((timeElapsed / MAX_READ_TIME) * 100, 100);
  const isCorrect = selectedAnswer === item.question?.correct;

  return (
    <Card className={`border ${relevanceStyle.border} shadow-sm mb-6`}>
      {/* Header with Theme & Badge */}
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div>
            <Badge className={`${relevanceStyle.bg} ${relevanceStyle.text} mb-2`}>
              {relevanceStyle.icon} {relevanceStyle.label} — {relevanceStyle.description}
            </Badge>
            <CardTitle className="text-lg font-semibold text-slate-800">
              {item.theme}
            </CardTitle>
          </div>
          
          {/* Timer */}
          <div className="text-right">
            <div className={`text-sm font-mono ${timeElapsed >= MAX_READ_TIME ? 'text-red-600' : 'text-slate-500'}`}>
              <Clock className="w-4 h-4 inline mr-1" />
              {formatTime(timeElapsed)}
            </div>
            <Progress 
              value={timeProgress} 
              className={`w-20 h-1 mt-1 ${timeElapsed >= MAX_READ_TIME ? '[&>div]:bg-red-500' : ''}`}
            />
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Time Exceeded Warning */}
        {timeElapsed >= MAX_READ_TIME && (
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <p className="text-sm text-amber-800 font-medium flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" />
              More time here will not improve exam outcomes.
            </p>
          </div>
        )}

        {/* Why UPSC Cares */}
        <div className="p-3 bg-slate-50 rounded-lg">
          <p className="text-xs text-slate-500 mb-1">Why UPSC Cares</p>
          <p className="text-sm text-slate-700">{item.why_upsc_cares}</p>
        </div>

        {/* Context Bullets - Collapsible after 3 mins */}
        {!isCollapsed ? (
          <div className="space-y-2">
            <p className="text-xs text-slate-500">Minimum Context</p>
            <ul className="space-y-1">
              {item.context_bullets?.map((bullet, idx) => (
                <li key={idx} className="flex items-start gap-2 text-sm text-slate-700">
                  <span className="text-slate-400 mt-1">•</span>
                  {bullet}
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <button 
            onClick={() => setIsCollapsed(false)}
            className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-700"
          >
            <ChevronDown className="w-4 h-4" />
            Show context (not recommended)
          </button>
        )}

        {/* Data Point */}
        {item.data_point && !isCollapsed && (
          <div className="p-2 bg-blue-50 border border-blue-100 rounded text-sm text-blue-800">
            <strong>Key Data:</strong> {item.data_point}
          </div>
        )}

        {/* Question Section - Always Visible */}
        <div className="border-t pt-4 mt-4">
          <p className="text-xs text-slate-500 mb-2 flex items-center gap-1">
            <Lightbulb className="w-3 h-3" />
            Application Question (Mandatory)
          </p>
          
          {item.question?.type === 'mcq' && (
            <div className="space-y-3">
              <p className="text-sm font-medium text-slate-800">{item.question.stem}</p>
              
              <RadioGroup 
                value={selectedAnswer} 
                onValueChange={setSelectedAnswer}
                disabled={showResult}
              >
                {Object.entries(item.question.options || {}).map(([key, value]) => (
                  <div 
                    key={key} 
                    className={`flex items-center space-x-2 p-2 rounded ${
                      showResult && key === item.question.correct 
                        ? 'bg-emerald-50 border border-emerald-200' 
                        : showResult && key === selectedAnswer && !isCorrect
                        ? 'bg-red-50 border border-red-200'
                        : 'hover:bg-slate-50'
                    }`}
                  >
                    <RadioGroupItem value={key} id={`${item.id}-${key}`} />
                    <Label htmlFor={`${item.id}-${key}`} className="text-sm cursor-pointer flex-1">
                      ({key}) {value}
                    </Label>
                    {showResult && key === item.question.correct && (
                      <CheckCircle className="w-4 h-4 text-emerald-600" />
                    )}
                    {showResult && key === selectedAnswer && !isCorrect && (
                      <XCircle className="w-4 h-4 text-red-600" />
                    )}
                  </div>
                ))}
              </RadioGroup>

              {!showResult ? (
                <Button 
                  onClick={handleAnswerSubmit}
                  disabled={!selectedAnswer}
                  className="w-full mt-2"
                  size="sm"
                >
                  Check Answer
                </Button>
              ) : (
                <div className={`p-3 rounded-lg mt-2 ${isCorrect ? 'bg-emerald-50' : 'bg-amber-50'}`}>
                  <p className={`text-sm font-medium ${isCorrect ? 'text-emerald-700' : 'text-amber-700'}`}>
                    {isCorrect ? '✓ Correct!' : '✗ Incorrect'}
                  </p>
                  <p className="text-xs text-slate-600 mt-1">{item.question.explanation}</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Completion */}
        {showResult && (
          <div className="pt-3 border-t">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => onComplete(item.id)}
              className="w-full"
            >
              Done — Move to next
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ============================================================================
// MAIN ERCA PAGE
// ============================================================================

const ERCAPage = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [items, setItems] = useState([]);
  const [completedIds, setCompletedIds] = useState([]);

  useEffect(() => {
    fetchERCAData();
  }, []);

  const fetchERCAData = async () => {
    setLoading(true);
    const token = localStorage.getItem('token');

    try {
      // Fetch status first
      const statusRes = await managedAxios.get(`${API_BASE}/erca/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStatus(statusRes.data);

      // If cap not reached, fetch items
      if (!statusRes.data.cap_reached) {
        const itemsRes = await managedAxios.get(`${API_BASE}/erca/items?limit=${statusRes.data.remaining}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setItems(itemsRes.data.items || []);
        setStatus(prev => ({
          ...prev,
          daily_count: itemsRes.data.daily_count,
          cap_reached: itemsRes.data.cap_reached
        }));
      }
    } catch (err) {
      console.error('ERCA fetch error:', err);
      // Show sample items if API fails
      setItems(getSampleItems());
    } finally {
      setLoading(false);
    }
  };

  const handleItemComplete = async (itemId) => {
    setCompletedIds(prev => [...prev, itemId]);
    
    // Record completion to backend (counts toward daily cap)
    try {
      const token = localStorage.getItem('token');
      await managedAxios.post(`${API_BASE}/erca/complete/${itemId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Refresh status
      const statusRes = await managedAxios.get(`${API_BASE}/erca/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStatus(statusRes.data);
    } catch (err) {
      console.error('Failed to record completion:', err);
    }
    
    toast({
      title: "Item completed",
      description: "You are not missing out. You are preparing correctly."
    });
  };

  const visibleItems = items.filter(item => !completedIds.includes(item.id));

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <p className="text-slate-500">Loading exam-relevant CA...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-2xl mx-auto p-6 py-10">
        
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg bg-slate-800 flex items-center justify-center">
              <Filter className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-semibold text-slate-900">Exam-Relevant Current Affairs</h1>
              <p className="text-sm text-slate-500">Theme-based • Application-focused • Time-controlled</p>
            </div>
          </div>
        </div>

        {/* Philosophy Banner */}
        <div className="p-4 bg-slate-100 rounded-xl mb-6">
          <p className="text-sm text-slate-700 font-medium">
            CLARIAS filters current affairs so you don't have to chase them.
          </p>
          <p className="text-xs text-slate-500 mt-1">
            Reading more CA does NOT increase Prelims probability. Application matters.
          </p>
        </div>

        {/* Daily Status */}
        <div className="flex items-center justify-between p-4 bg-white border border-slate-200 rounded-lg mb-6">
          <div>
            <p className="text-xs text-slate-500">Today's Exposure</p>
            <p className="text-lg font-semibold text-slate-800">
              {status?.daily_count || 0} / {DAILY_CAP}
            </p>
          </div>
          <div className="text-right">
            <Progress 
              value={(status?.daily_count || 0) / DAILY_CAP * 100} 
              className="w-24 h-2"
            />
            <p className="text-xs text-slate-500 mt-1">
              {status?.remaining || DAILY_CAP} remaining
            </p>
          </div>
        </div>

        {/* Cap Reached Message */}
        {status?.cap_reached && (
          <Card className="border-emerald-200 bg-emerald-50 mb-6">
            <CardContent className="p-6 text-center">
              <Shield className="w-12 h-12 text-emerald-600 mx-auto mb-3" />
              <h3 className="font-semibold text-emerald-800 mb-2">
                Your CA exposure is sufficient for today.
              </h3>
              <p className="text-sm text-emerald-700">
                More time here will not improve exam outcomes.
              </p>
              <p className="text-xs text-emerald-600 mt-3 italic">
                You are not missing out. You are preparing correctly.
              </p>
            </CardContent>
          </Card>
        )}

        {/* ERCA Items */}
        {!status?.cap_reached && visibleItems.length > 0 && (
          <div className="space-y-4">
            {visibleItems.map(item => (
              <ERCAItemCard 
                key={item.id} 
                item={item} 
                onComplete={handleItemComplete}
              />
            ))}
          </div>
        )}

        {/* No Items Available */}
        {!status?.cap_reached && visibleItems.length === 0 && (
          <Card className="border-slate-200">
            <CardContent className="p-6 text-center">
              <p className="text-slate-500">No new exam-relevant CA available.</p>
              <p className="text-xs text-slate-400 mt-2">
                This is intentional. Most current affairs are safely ignorable.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Relevance Legend */}
        <div className="mt-8 p-4 bg-slate-50 rounded-lg">
          <p className="text-xs text-slate-500 mb-3">Relevance Badges — To reduce FOMO</p>
          <div className="grid grid-cols-3 gap-2">
            {Object.entries(RELEVANCE_STYLES).map(([key, style]) => (
              <div key={key} className={`p-2 rounded ${style.bg} text-center`}>
                <span className="text-lg">{style.icon}</span>
                <p className={`text-xs font-medium ${style.text}`}>{style.label}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Identity Statement */}
        <p className="text-center text-xs text-slate-400 mt-8">
          Current affairs are a tool, not a task.
        </p>
      </div>
    </div>
  );
};

// Sample items for demo when API is empty
const getSampleItems = () => [
  {
    id: "erca_sample_1",
    theme: "Economy → Banking Sector Reforms",
    why_upsc_cares: "This affects elimination in policy-application questions about financial inclusion.",
    context_bullets: [
      "RBI's revised framework focuses on strengthening governance in commercial banks",
      "New norms require enhanced disclosure of related-party transactions",
      "Timeline for compliance set for scheduled commercial banks"
    ],
    data_point: "₹2.5 lakh crore NPAs resolved through IBC in FY24",
    question: {
      type: "mcq",
      stem: "Consider the following statements about banking sector reforms:\n1. The Insolvency and Bankruptcy Code applies only to corporate debtors.\n2. RBI can supersede the board of a banking company under certain conditions.\nWhich of the above is/are correct?",
      options: {
        "A": "1 only",
        "B": "2 only",
        "C": "Both 1 and 2",
        "D": "Neither 1 nor 2"
      },
      correct: "B",
      explanation: "IBC applies to individuals and partnerships too, not just corporates. RBI has powers under Banking Regulation Act to supersede boards."
    },
    relevance: "moderate"
  },
  {
    id: "erca_sample_2",
    theme: "IR → India-ASEAN Relations",
    why_upsc_cares: "Useful for elimination in questions about Look East/Act East policy evolution.",
    context_bullets: [
      "India-ASEAN relations elevated to Comprehensive Strategic Partnership",
      "Focus areas include connectivity, digital economy, and maritime security",
      "ASEAN centrality remains key principle in Indo-Pacific architecture"
    ],
    data_point: null,
    question: {
      type: "mcq",
      stem: "Which of the following correctly describes India's engagement with ASEAN?",
      options: {
        "A": "India is a founding member of ASEAN",
        "B": "India became a Summit-level partner in 2002",
        "C": "RCEP includes India as a member",
        "D": "India-ASEAN FTA covers only goods trade"
      },
      correct: "B",
      explanation: "India became ASEAN's Summit-level partner in 2002. India is not a founding member, withdrew from RCEP, and the FTA covers goods, services, and investments."
    },
    relevance: "moderate"
  },
  {
    id: "erca_sample_3",
    theme: "Polity → Judicial Appointments",
    why_upsc_cares: "Direct application in questions about collegium system and separation of powers.",
    context_bullets: [
      "Collegium system established through Second and Third Judges Cases",
      "Recent discussions on transparency in judicial appointments",
      "NJAC struck down preserving collegium primacy"
    ],
    data_point: null,
    question: {
      type: "mcq",
      stem: "The collegium system for appointment of judges was established through:",
      options: {
        "A": "Constitutional amendment",
        "B": "Parliamentary legislation",
        "C": "Judicial interpretation",
        "D": "Executive order"
      },
      correct: "C",
      explanation: "The collegium system emerged through judicial interpretation in the Second Judges Case (1993) and was refined in the Third Judges Case (1998)."
    },
    relevance: "high"
  }
];

export default ERCAPage;
